import { v3 } from "../short3";
import { Face3, Geometry } from "oc-threejs/geometries/Geometry";

import {
  BackSide,
  BufferGeometry,
  DoubleSide,
  Material,
  Matrix4,
  Mesh,
  Object3D,
  Sphere,
  Triangle,
  Vector3,
} from "three";

//statische Hilfsvariablen, die in isIntersectingTriangle benutzt werden,
//aber aus Performancegründen nicht bei jedem Methodenaufruf neu erzeugt werden

const vA = v3();
const vB = v3();
const vC = v3();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tempA = v3();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tempB = v3();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tempC = v3();

/**
 * Abstrakte Basisklasse für alle IntersectionHandler
 * @see http://preview.objectcode.de/oc-base Examples/#oc-threejs.TestIntersectionHandler
 */
export abstract class AbstractIntersectionHandler {
  objects: Object3D | Object3D[];
  recursive: boolean;
  boundingSphere: Sphere;
  inverseMatrix: Matrix4;
  intersectionsOfCurrentObject: any[];
  intersectioncallback: (intersection: any) => void;

  /**
   * @param options
   * @example
   * options = {
   *   // sollen auch Unterobjecte getestet werden?
   *   recursive: true,
   * }
   */
  constructor(objects: Object3D | Object3D[], options?: { recursive?: boolean }) {
    // größtenteils von Raycaster und Mesh geklaut

    this.objects = objects;
    this.recursive = (options && options.recursive) || true;

    this.boundingSphere = new Sphere();
    this.inverseMatrix = new Matrix4();

    this.intersectionsOfCurrentObject = [];
  }

  /**
   * Führt einen Schnittstest gegen mehrere Objecte durch
   */
  intersectObjects(objects: Object3D[]): void {
    objects.forEach((object) => this.intersectObject(object));
  }

  /**
   * Führt einen Schnittstest gegen ein einzelnes Object durch
   */
  intersectObject(object: Object3D): void {
    if (object.visible === false) {
      return;
    }
    if (object instanceof Mesh) {
      const geometry = object.geometry;
      const material = object.material;

      if (!material) {
        return;
      }

      if (geometry.boundingSphere === null) {
        geometry.computeBoundingSphere();
      }

      const matrixWorld = object.matrixWorld;

      this.boundingSphere.copy(geometry.boundingSphere);
      this.boundingSphere.applyMatrix4(matrixWorld);

      if (!this.isIntersectionSphere(this.boundingSphere)) {
        return;
      }

      this.inverseMatrix.copy(matrixWorld).invert();

      (this.getGeometryInLocalSpace() as unknown as any) // Cast needed as it's abstract
        .copy(this.getGeometryInWorldSpace())
        .applyMatrix4(this.inverseMatrix);

      this.intersectionsOfCurrentObject.length = 0;

      if (geometry instanceof BufferGeometry) {
        //console.log('BufferGeometry');
        this.intersectBufferGeometry(object as Mesh);
      } else if (geometry instanceof Geometry) {
        //console.log('Geometry');
        this.intersectGeometry(object as Mesh);
      }

      this.flushIntersectionsOfCurrentObject(this.intersectionsOfCurrentObject);
    }
    if (this.recursive) {
      this.intersectObjects(object.children);
    }
  }

  /**
   * interne Methode
   */
  intersectBufferGeometry(object: Mesh): void {
    let a, b, c;
    const geometry = object.geometry as BufferGeometry;
    const index = geometry.index;
    const attributes = geometry.attributes;
    const positions = attributes?.position?.array;
    if (!positions) {
      return;
    }

    if (index !== null) {
      const indices = index.array;
      for (let i = 0, l = indices.length; i < l; i += 3) {
        a = indices[i];
        b = indices[i + 1];
        c = indices[i + 2];
        this.checkBufferGeometryIntersection(
          object,
          positions as Float32Array | ArrayLike<number>,
          a,
          b,
          c,
          (intersection: any) => {
            // triangle number in indices buffer semantics
            intersection.faceIndex = Math.floor(i / 3);
          },
        );
      }
    } else {
      for (let i = 0, l = positions.length; i < l; i += 9) {
        a = i / 3;
        b = a + 1;
        c = a + 2;
        this.checkBufferGeometryIntersection(
          object,
          positions as Float32Array | ArrayLike<number>,
          a,
          b,
          c,
          (intersection: any) => {
            // triangle number in positions buffer semantics
            intersection.index = a;
          },
        );
      }
    }
  }

  /**
   * interne Methode
   */
  checkBufferGeometryIntersection(
    object: Mesh,
    positions: any,
    a: number,
    b: number,
    c: number,
    enhanceIntersectionCallback: (i: any) => void,
  ): void {
    vA.fromArray(positions, a * 3);
    vB.fromArray(positions, b * 3);
    vC.fromArray(positions, c * 3);

    this.checkIntersection(object, vA, vB, vC, (intersection: any) => {
      intersection.faceIndex = a;
      intersection.face = new Face3(a, b, c, Triangle.getNormal(vA, vB, vC, v3()));
      enhanceIntersectionCallback(intersection);
    });
  }

  /**
   * interne Methode
   */
  intersectGeometry(object: Mesh): void {
    let fvA, fvB, fvC;
    const geometry = object.geometry as unknown as Geometry;
    const material = object.material;
    const materials = Array.isArray(material)
      ? (index: number) => material[index]
      : (index: number) => material;

    const vertices = geometry.vertices;
    const faces = geometry.faces;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const faceVertexUvs = geometry.faceVertexUvs[0];

    for (let f = 0, fl = faces.length; f < fl; f++) {
      const face = faces[f];
      const faceMaterial = materials(face.materialIndex);

      if (faceMaterial === undefined) {
        continue;
      }

      fvA = vertices[face.a];
      fvB = vertices[face.b];
      fvC = vertices[face.c];

      this.checkIntersection(object, fvA, fvB, fvC, (intersection: any) => {
        intersection.face = face;
        intersection.faceIndex = f;
      });
    }
  }

  /**
   * interne Methode
   */
  flushIntersectionsOfCurrentObject(intersectionsOfCurrentObject: any[]): void {
    if (this.intersectioncallback) {
      intersectionsOfCurrentObject.forEach(this.intersectioncallback);
    }
  }

  /**
   * interne Methode
   */
  // Default callback if none provided (though code calls it, better to have default or check)
  // intersectioncallback(i) { console.log(i); } // Property now

  /**
   * interne Methode
   */
  checkIntersection(
    object: Mesh,
    pA: Vector3,
    pB: Vector3,
    pC: Vector3,
    enhanceIntersectionCallback: (intersection: any) => void,
  ): void {
    let a: Vector3, b: Vector3, c: Vector3, backfaceCulling: boolean;
    const material = object.material as Material;

    if (material.side === BackSide) {
      a = pC;
      b = pB;
      c = pA;
      backfaceCulling = true;
    } else {
      a = pA;
      b = pB;
      c = pC;
      backfaceCulling = material.side !== DoubleSide;
    }

    this.intersectTriangle(a, b, c, backfaceCulling, object.matrixWorld, (intersection: any) => {
      enhanceIntersectionCallback(intersection);
      intersection.object = object;
      this.intersectionsOfCurrentObject.push(intersection);
    });
  }

  // abstract methods ----

  /**
   * abstrakte Methode
   */
  abstract isIntersectionSphere(sphere: Sphere): boolean;

  /**
   * abstrakte Methode
   */
  abstract intersectTriangle(
    a: Vector3,
    b: Vector3,
    c: Vector3,
    backfaceCulling: boolean,
    worldMatrix: Matrix4,
    pushIntersectionCallback: (intersection: any) => void,
  ): void;

  /**
   * abstrakte Methode
   */
  abstract getGeometryInLocalSpace(): any;

  /**
   * abstrakte Methode
   */
  abstract getGeometryInWorldSpace(): any;

  /**
   * abstrakte Methode
   */
  debugShow(): any {
    return null;
  }

  // ---------------------

  /**
   * interne Methode
   */
  applyMatrixOnPoints(matrix: Matrix4, points: Vector3[]): Vector3[] {
    points.forEach((p) => p.applyMatrix4(matrix));
    return points;
  }

  /**
   * interne Methode
   */
  intersect(intersectioncallback: (intersection: any) => void): void {
    this.intersectioncallback = intersectioncallback;
    if (this.objects instanceof Object3D) {
      this.objects.updateMatrixWorld();
      this.intersectObject(this.objects);
    } else {
      (this.objects as Object3D[]).forEach((o) => o.updateMatrixWorld());
      this.intersectObjects(this.objects as Object3D[]);
    }
  }
}
